// Runs (/runs; Mitch, 2026-11-04: "a simplified manufacturing space where we focus on production runs and the purchase
// orders that go with them"): where runs live. /runs is every project's runs in one table (RunRows.tsx), `?project=`
// one project's (its header links here: "3 runs · 1 open"); /runs/PR-001 is a run's own page (RunWorkspace.tsx), so
// the sidebar stays on Runs and a project's page is only its setup. New run picks the project (none to pick with
// ?project=), then asks to plan one or record a past one.
import { History } from "lucide-react";
import { useMemo, useState } from "react";
import type { Project } from "../../../../../shared/db.gen.ts";
import { projectByCode, projectPath, projectScope, readiness } from "../../../../../shared/projects.ts";
import { runPath } from "../../../../../shared/runs.ts";
import { can } from "../../../lib/mutate.ts";
import { useProjectIndex } from "../../../lib/projects.ts";
import { Link, navigate, setSearch, usePath, useSearch } from "../../../lib/router.tsx";
import { useRows } from "../../../lib/sync.ts";
import { Button, EmptyState, Select } from "../../../ui/controls.tsx";
import { DialogLayer } from "../../../ui/DialogLayer.tsx";
import { ListPage } from "../../../ui/ListPage.tsx";
import { runColumns, useRunRows } from "./RunRows.tsx";
import { NewRunDialog, RunWorkspace } from "./RunWorkspace.tsx";

export function RunsPage() {
  const path = usePath();
  const number = decodeURIComponent(path.replace(/^\/runs\/?/, "").split("/")[0] ?? "");
  return number ? <RunPage key={number} number={number} /> : <RunList />;
}

/** One run's page, found by its number (PR-001) or id. */
function RunPage({ number }: { number: string }) {
  const runs = useRows("production_run");
  const projects = useRows("project");
  const px = useProjectIndex();
  const run = runs.find((r) => r.number === number || r.id === number);
  const project = run ? projects.find((p) => p.id === run.project_id) : undefined;
  const scope = project ? projectScope(px, project.id) : null;
  const r = project ? readiness(px, project.id) : null;
  if (!run || !project || !scope || !r)
    return (
      <EmptyState icon={<History className="size-8" />} title={`No run ${number}`}>
        <Link to="/runs" className="text-accent-text">
          All runs
        </Link>
      </EmptyState>
    );
  return (
    <div className="flex h-full min-h-0 flex-col">
      <RunWorkspace project={project} px={px} scope={scope} r={r} here={runPath(run)} run={run} />
    </div>
  );
}

function RunList() {
  const runs = useRows("production_run");
  const projects = useRows("project");
  const vendors = useRows("vendor");
  const params = useSearch();
  const only = params.get("project") ? projectByCode(projects, params.get("project") ?? "") : undefined;
  const rows = useRunRows(only ? runs.filter((r) => r.project_id === only.id) : runs);
  const byId = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);
  const columns = useMemo(() => {
    const vendorById = new Map(vendors.map((v) => [v.id, v]));
    return runColumns({ projects: byId, cmName: (id) => (id ? vendorById.get(id)?.short_name || vendorById.get(id)?.name || "" : "") });
  }, [byId, vendors]);
  const live = projects.filter((p) => p.status !== "archived" || p.id === only?.id).sort((a, b) => a.name.localeCompare(b.name));
  // New run: the project first (unless the list is one project's), then plan or record a past one
  const [picking, setPicking] = useState(false);
  const [creating, setCreating] = useState<Project | null>(null);
  return (
    <>
      <ListPage
        title="Runs"
        scope={
          <span className="w-48 shrink-0">
            <Select
              aria-label="Project"
              value={only?.id ?? ""}
              onChange={(e) => {
                const p = byId.get(e.target.value);
                setSearch({ project: p ? p.code || p.id : null });
              }}
              options={[{ value: "", label: "Every project" }, ...live.map((p) => ({ value: p.id, label: p.name }))]}
              className="h-7 text-[12px]"
            />
          </span>
        }
        rows={rows}
        columns={columns}
        defaultSort={{ key: "number", desc: true }}
        onOpen={(x) => navigate(runPath(x.run))}
        newLabel="New run"
        onNew={can("production_run.create") ? () => (only ? setCreating(only) : setPicking(true)) : undefined}
        empty={
          <div className="p-8 text-center text-muted">
            {only ? `No runs of ${only.name} yet. ` : "No runs yet. "}A run is one batch at one CM: its units, the make order and parts POs, their invoices and
            payments, and what a unit cost.
          </div>
        }
      />
      {picking && (
        <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && setPicking(false)} onKeyDown={(e) => e.key === "Escape" && setPicking(false)}>
          <div data-dialog className="w-[360px] rounded-xl border border-line bg-surface p-3 shadow-pop">
            <div className="mb-1 px-1 text-[15px] font-semibold">New run of…</div>
            <div className="mb-2 px-1 text-[12px] text-muted">A run builds one project's products at one CM.</div>
            <div className="flex flex-col gap-0.5">
              {live.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    setPicking(false);
                    setCreating(p);
                  }}
                  className="flex items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-hover"
                >
                  <span className="w-16 shrink-0 font-mono text-[12px] text-muted">{p.code}</span>
                  <span className="truncate">{p.name}</span>
                </button>
              ))}
            </div>
            <div className="mt-2 flex justify-end">
              <Button variant="ghost" onClick={() => setPicking(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogLayer>
      )}
      {creating && <NewRun project={creating} onClose={() => setCreating(null)} />}
    </>
  );
}

/** New run of a project: a line per variant of its products, units set on the run's Units tab. */
function NewRun({ project, onClose }: { project: Project; onClose: () => void }) {
  const px = useProjectIndex();
  const variants = (projectScope(px, project.id)?.products ?? []).flatMap((p) => p.variants.map((v) => v.id));
  if (!variants.length)
    return (
      <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && onClose()} onKeyDown={(e) => e.key === "Escape" && onClose()}>
        <div data-dialog className="w-[400px] rounded-xl border border-line bg-surface p-4 shadow-pop">
          <div className="mb-1 text-[15px] font-semibold">New run of {project.name}</div>
          <div className="text-[12px] text-muted">
            It has no products yet: link them in{" "}
            <Link to={projectPath(project, "products")} className="text-accent-text hover:underline">
              its Products step
            </Link>
            , then start its run.
          </div>
          <div className="mt-3 flex justify-end">
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </DialogLayer>
    );
  return <NewRunDialog project={project} variants={variants} onClose={onClose} />;
}
